import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    OnInit,
    computed,
    inject,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { DesignService } from '../../services/design.service';
import { SearchService } from '../../services/search.service';
import type { ISearchParams } from '../../interfaces/ISearchParams';
import { TranslatePipe } from '@ngx-translate/core';
import { SiFormItemComponent } from '@siemens/element-ng/form';

@Component({
    selector: 'app-search-box',
    templateUrl: './search-box.component.html',
    styleUrls: ['./search-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, ReactiveFormsModule, SiFormItemComponent, TranslatePipe]
})
export class SearchBoxComponent implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly searchService = inject(SearchService);
    private readonly designService = inject(DesignService);
    private readonly router = inject(Router);

    protected readonly isAdvancedSearch = signal<boolean>(false);
    protected readonly appName = computed(() => this.designService.designSet().appName);

    private readonly searchStringState = signal<string>('');
    protected searchForm!: UntypedFormGroup;

    protected get searchString(): string {
        return this.searchStringState();
    }

    protected set searchString(value: string) {
        this.searchStringState.set(value ?? '');
    }

    @HostListener('window:scroll', ['$event'])
    protected onWindowScroll(_event: Event): void {
        const threshold = window?.devicePixelRatio < 1 ? 29 : 30;

        if (document.documentElement.scrollTop > threshold) {
            document.getElementById('search-box')?.classList.add('scrolled');
            return;
        }

        if (!window.location.pathname.includes('map') && !window.location.pathname.includes('location/detail')) {
            document.getElementById('search-box')?.classList.remove('scrolled');
        }
    }

    public ngOnInit(): void {
        this.initSearchForm();
        this.initAdvancedSearchStateListener();
    }

    protected resetFilters(): void {
        this.searchForm.reset();
        this.searchService.storeSearchParams(null);
    }

    protected search(isAdvancedSearch: boolean, _event?: Event): void {
        let searchParams: ISearchParams;
        if (!isAdvancedSearch) {
            searchParams = { isAdvancedSearch: false, searchString: this.searchString };
        } else {
            searchParams = { isAdvancedSearch: true, advancedSearchParams: this.searchForm.value };
        }
        this.searchService.storeSearchParams(searchParams);
        void this.router.navigate(['results']);
    }

    protected toggleAdvancedSearch(): void {
        const nextState = !this.isAdvancedSearch();
        this.searchService.storeSearchBoxState(nextState);
    }

    private initSearchForm(): void {
        this.searchForm = new UntypedFormGroup({
            firm: new UntypedFormControl(),
            country: new UntypedFormControl(),
            city: new UntypedFormControl(),
            loccode: new UntypedFormControl(),
            postalcode: new UntypedFormControl(),
            address: new UntypedFormControl(),
            locationshort: new UntypedFormControl(),
            section: new UntypedFormControl(),
            are: new UntypedFormControl(),
            corporatetype: new UntypedFormControl(),
            businessactivity: new UntypedFormControl(),
            client: new UntypedFormControl(),
            headquarter: new UntypedFormControl()
        });
    }

    private initAdvancedSearchStateListener(): void {
        this.searchService.currentSearchBoxState
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(value => this.isAdvancedSearch.set(value))
            )
            .subscribe();
    }
}
